/**
 * User Profile Page Component
 * Allows users to view and edit their profile information
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import dayjs from 'dayjs';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Avatar, 
  Typography, 
  Alert, 
  Space,
  Tag,
  Descriptions,
  Spin,
  Modal,
  message,
  Tooltip,
  Steps,
  QRCode,
  Divider,
  List,
  DatePicker,
  Select,
  Empty
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined,
  LockOutlined,
  CameraOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  CrownOutlined,
  TeamOutlined,
  BookOutlined,
  ReadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  IdcardOutlined,
  CalendarOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined,
  ExclamationCircleOutlined,
  MobileOutlined,
  KeyOutlined,
  ReloadOutlined,
  CopyOutlined,
  WarningOutlined,
  LaptopOutlined,
  DesktopOutlined,
  LogoutOutlined,
  HistoryOutlined,
  EyeOutlined,
  FilterOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import { 
  AppRoles, 
  changePassword, 
  uploadProfileImage, 
  deleteProfileImage, 
  updateProfile,
  get2FASetup,
  enable2FA,
  disable2FA,
  get2FAStatus,
  regenerateRecoveryCodes,
  getSessions,
  revokeSession,
  revokeOtherSessions,
  revokeAllSessions,
  getTrustedDevices,
  revokeTrustedDevice,
  revokeAllTrustedDevices,
  getLoginHistory
} from '../../services/authService';
import { getProfileImageUrl } from '../../utils/urlUtils';
import PagesWrapper from '../../PagesWrapper';
import styles from './Profile.module.css';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Available languages
const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

/**
 * Get role display configuration
 */
const getRoleConfig = (role) => {
  const configs = {
    [AppRoles.Admin]: { 
      label: 'Admin', 
      color: '#f5222d', 
      icon: <CrownOutlined /> 
    },
    [AppRoles.ContentCreator]: { 
      label: 'Content Creator', 
      color: '#722ed1', 
      icon: <EditOutlined /> 
    },
    [AppRoles.MasterTrainer]: { 
      label: 'Master Trainer', 
      color: '#fa8c16', 
      icon: <TeamOutlined /> 
    },
    [AppRoles.Trainer]: { 
      label: 'Trainer', 
      color: '#1890ff', 
      icon: <BookOutlined /> 
    },
    [AppRoles.Trainee]: { 
      label: 'Trainee', 
      color: '#52c41a', 
      icon: <ReadOutlined /> 
    },
  };
  return configs[role] || { label: role, color: '#8c8c8c', icon: <UserOutlined /> };
};

/**
 * Get user initials from name
 */
const getInitials = (firstName, lastName, email) => {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) {
    return firstName.substring(0, 2).toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  return 'U';
};

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * User Profile Page
 */
const Profile = () => {
  const { t } = useTranslation();
  const { user, refreshUser, loading: authLoading } = useAuth();
  
  // Edit mode states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  
  // Loading states
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);
  
  // Error states
  const [profileError, setProfileError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  
  // 2FA states
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorSetupData, setTwoFactorSetupData] = useState(null);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [setupStep, setSetupStep] = useState(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [recoveryCodesRemaining, setRecoveryCodesRemaining] = useState(0);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [loading2FA, setLoading2FA] = useState(false);
  const [error2FA, setError2FA] = useState(null);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  
  // Session management states
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [revokingSession, setRevokingSession] = useState(null);
  const [revokingOthers, setRevokingOthers] = useState(false);
  const [revokingAll, setRevokingAll] = useState(false);
  
  // Trusted devices states (Remember Device for 30 days)
  const [trustedDevices, setTrustedDevices] = useState([]);
  const [loadingTrustedDevices, setLoadingTrustedDevices] = useState(false);
  const [revokingTrustedDevice, setRevokingTrustedDevice] = useState(null);
  const [revokingAllTrustedDevices, setRevokingAllTrustedDevices] = useState(false);
  
  // Login history states
  const [loginHistory, setLoginHistory] = useState([]);
  const [loginHistoryCount, setLoginHistoryCount] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  // Default to last month
  const [historyDateRange, setHistoryDateRange] = useState(() => [
    dayjs().subtract(1, 'month'),
    dayjs()
  ]);
  const [historyEventFilter, setHistoryEventFilter] = useState('all');
  
  // Form refs
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const fileInputRef = useRef(null);

  // Get user initials for avatar fallback
  const initials = user ? getInitials(user.firstName, user.lastName, user.email) : 'U';
  
  // Avatar background color based on email
  const avatarColor = React.useMemo(() => {
    if (!user?.email) return '#1890ff';
    const colors = ['#1890ff', '#13c2c2', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96'];
    const hash = user.email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  }, [user?.email]);

  // Profile image URL
  const profileImageUrl = getProfileImageUrl(user);

  /**
   * Handle profile form submit
   */
  const handleProfileSubmit = async (values) => {
    setSavingProfile(true);
    setProfileError(null);
    
    try {
      await updateProfile(values);
      // Change app language if user selected a different one
      if (values.preferredLanguage && values.preferredLanguage !== i18n.language) {
        await i18n.changeLanguage(values.preferredLanguage);
      }
      await refreshUser();
      setIsEditingProfile(false);
      message.success(t('profile.profileUpdated'));
    } catch (err) {
      setProfileError(err.message || t('profile.updateFailed'));
    } finally {
      setSavingProfile(false);
    }
  };

  /**
   * Handle password form submit
   */
  const handlePasswordSubmit = async (values) => {
    setSavingPassword(true);
    setPasswordError(null);
    
    try {
      await changePassword(values.currentPassword, values.newPassword, values.confirmPassword);
      setIsEditingPassword(false);
      passwordForm.resetFields();
      message.success(t('profile.passwordChanged'));
    } catch (err) {
      setPasswordError(err.message || t('profile.passwordChangeFailed'));
    } finally {
      setSavingPassword(false);
    }
  };

  /**
   * Handle profile image upload
   */
  const handleImageUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      message.error(t('profile.invalidImageType'));
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error(t('profile.imageTooLarge'));
      return;
    }
    
    setUploadingImage(true);
    try {
      await uploadProfileImage(file);
      await refreshUser();
      message.success(t('profile.imageUploaded'));
    } catch (err) {
      message.error(err.message || t('profile.imageUploadFailed'));
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [refreshUser, t]);

  /**
   * Handle profile image delete
   */
  const handleImageDelete = useCallback(() => {
    Modal.confirm({
      title: t('profile.deleteImageTitle'),
      content: t('profile.deleteImageConfirm'),
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      okText: t('common.delete'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setDeletingImage(true);
        try {
          await deleteProfileImage();
          await refreshUser();
          message.success(t('profile.imageDeleted'));
        } catch (err) {
          message.error(err.message || t('profile.imageDeleteFailed'));
        } finally {
          setDeletingImage(false);
        }
      }
    });
  }, [refreshUser, t]);

  /**
   * Start editing profile
   */
  const startEditProfile = () => {
    profileForm.setFieldsValue({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phoneNumber: user?.phoneNumber || '',
      preferredLanguage: user?.preferredLanguage || i18n.language || 'en',
    });
    setProfileError(null);
    setIsEditingProfile(true);
  };

  /**
   * Cancel editing profile
   */
  const cancelEditProfile = () => {
    profileForm.resetFields();
    setProfileError(null);
    setIsEditingProfile(false);
  };

  /**
   * Start editing password
   */
  const startEditPassword = () => {
    passwordForm.resetFields();
    setPasswordError(null);
    setIsEditingPassword(true);
  };

  /**
   * Cancel editing password
   */
  const cancelEditPassword = () => {
    passwordForm.resetFields();
    setPasswordError(null);
    setIsEditingPassword(false);
  };

  /**
   * Load 2FA status on mount
   */
  useEffect(() => {
    const load2FAStatus = async () => {
      try {
        const status = await get2FAStatus();
        setTwoFactorEnabled(status.isEnabled);
        setRecoveryCodesRemaining(status.recoveryCodesRemaining || 0);
      } catch (err) {
        console.error('Failed to load 2FA status:', err);
      }
    };
    if (user) {
      load2FAStatus();
    }
  }, [user]);

  /**
   * Load active sessions
   */
  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const response = await getSessions();
      setSessions(response.sessions || []);
      setCurrentSessionId(response.currentSessionId);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      message.error(t('profile.sessions.loadFailed'));
    } finally {
      setLoadingSessions(false);
    }
  }, [t]);

  /**
   * Load trusted devices
   */
  const loadTrustedDevices = useCallback(async () => {
    setLoadingTrustedDevices(true);
    try {
      const response = await getTrustedDevices();
      setTrustedDevices(response.devices || []);
    } catch (err) {
      console.error('Failed to load trusted devices:', err);
      message.error(t('profile.trustedDevices.loadFailed'));
    } finally {
      setLoadingTrustedDevices(false);
    }
  }, [t]);

  /**
   * Load login history
   */
  const loadLoginHistory = useCallback(async (dateRange = null) => {
    setLoadingHistory(true);
    try {
      // Use provided dateRange or current state
      const range = dateRange !== undefined ? dateRange : historyDateRange;
      const startDate = range && range[0] ? range[0].startOf('day').toDate() : null;
      const endDate = range && range[1] ? range[1].endOf('day').toDate() : null;
      
      const response = await getLoginHistory(startDate, endDate);
      setLoginHistory(response.events || []);
      setLoginHistoryCount(response.totalCount || response.events?.length || 0);
    } catch (err) {
      console.error('Failed to load login history:', err);
      message.error(t('profile.loginHistory.loadFailed'));
    } finally {
      setLoadingHistory(false);
    }
  }, [t, historyDateRange]);

  /**
   * Load sessions and trusted devices on mount (login history loads on demand when modal opens)
   */
  useEffect(() => {
    if (user) {
      loadSessions();
      loadTrustedDevices();
      loadLoginHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loadSessions, loadTrustedDevices]);

  /**
   * Revoke a specific session
   */
  const handleRevokeSession = async (sessionId) => {
    setRevokingSession(sessionId);
    try {
      await revokeSession(sessionId);
      await loadSessions();
    } catch (err) {
      console.error('Failed to revoke session:', err);
    } finally {
      setRevokingSession(null);
    }
  };

  /**
   * Revoke all other sessions
   */
  const handleRevokeOtherSessions = async () => {
    setRevokingOthers(true);
    try {
      await revokeOtherSessions();
      await loadSessions();
    } catch (err) {
      console.error('Failed to revoke other sessions:', err);
    } finally {
      setRevokingOthers(false);
    }
  };

  /**
   * Revoke all sessions (logout everywhere)
   */
  const handleRevokeAllSessions = async () => {
    Modal.confirm({
      title: t('profile.sessions.revokeAllConfirmTitle'),
      content: t('profile.sessions.revokeAllConfirmMessage'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      okType: 'danger',
      onOk: async () => {
        setRevokingAll(true);
        try {
          await revokeAllSessions();
          // User will be logged out, redirect will happen automatically
        } catch (err) {
          console.error('Failed to revoke all sessions:', err);
        } finally {
          setRevokingAll(false);
        }
      },
    });
  };

  /**
   * Revoke a specific trusted device
   */
  const handleRevokeTrustedDevice = async (deviceId) => {
    setRevokingTrustedDevice(deviceId);
    try {
      await revokeTrustedDevice(deviceId);
      await loadTrustedDevices();
    } catch (err) {
      console.error('Failed to revoke trusted device:', err);
    } finally {
      setRevokingTrustedDevice(null);
    }
  };

  /**
   * Revoke all trusted devices
   */
  const handleRevokeAllTrustedDevices = async () => {
    Modal.confirm({
      title: t('profile.trustedDevices.revokeAllConfirmTitle'),
      content: t('profile.trustedDevices.revokeAllConfirmMessage'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      okType: 'danger',
      onOk: async () => {
        setRevokingAllTrustedDevices(true);
        try {
          await revokeAllTrustedDevices();
          await loadTrustedDevices();
        } catch (err) {
          console.error('Failed to revoke all trusted devices:', err);
        } finally {
          setRevokingAllTrustedDevices(false);
        }
      },
    });
  };

  /**
   * Filter login history based on event type (date filtering is done server-side)
   */
  const filteredLoginHistory = React.useMemo(() => {
    let filtered = [...loginHistory];
    
    // Filter by event type
    if (historyEventFilter !== 'all') {
      if (historyEventFilter === 'success') {
        filtered = filtered.filter(event => event.success);
      } else if (historyEventFilter === 'failed') {
        filtered = filtered.filter(event => !event.success);
      } else {
        filtered = filtered.filter(event => event.eventType === historyEventFilter);
      }
    }
    
    return filtered;
  }, [loginHistory, historyEventFilter]);

  /**
   * Get unique event types for filter dropdown
   */
  const eventTypes = React.useMemo(() => {
    const types = new Set(loginHistory.map(event => event.eventType));
    return Array.from(types);
  }, [loginHistory]);

  /**
   * Get device icon based on device name
   */
  const getDeviceIcon = (deviceName) => {
    if (!deviceName) return <DesktopOutlined />;
    const lowerName = deviceName.toLowerCase();
    if (lowerName.includes('mobile') || lowerName.includes('iphone') || lowerName.includes('android')) {
      return <MobileOutlined />;
    }
    if (lowerName.includes('laptop')) {
      return <LaptopOutlined />;
    }
    return <DesktopOutlined />;
  };

  /**
   * Start 2FA setup wizard
   */
  const start2FASetup = async () => {
    setLoading2FA(true);
    setError2FA(null);
    try {
      const setupData = await get2FASetup();
      setTwoFactorSetupData(setupData);
      setShowSetupWizard(true);
      setSetupStep(0);
      setVerificationCode('');
    } catch (err) {
      setError2FA(err.message || t('profile.2faSetupFailed'));
    } finally {
      setLoading2FA(false);
    }
  };

  /**
   * Complete 2FA setup (verify code and enable)
   */
  const complete2FASetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError2FA(t('profile.enterValidCode'));
      return;
    }
    
    setLoading2FA(true);
    setError2FA(null);
    try {
      const result = await enable2FA(verificationCode);
      setRecoveryCodes(result.recoveryCodes || []);
      setTwoFactorEnabled(true);
      setRecoveryCodesRemaining(result.recoveryCodes?.length || 10);
      setSetupStep(2); // Move to recovery codes step
      message.success(t('profile.2faEnabled'));
    } catch (err) {
      setError2FA(err.message || t('profile.invalidVerificationCode'));
    } finally {
      setLoading2FA(false);
    }
  };

  /**
   * Close 2FA setup wizard
   */
  const close2FASetup = () => {
    setShowSetupWizard(false);
    setTwoFactorSetupData(null);
    setSetupStep(0);
    setVerificationCode('');
    setRecoveryCodes([]);
    setError2FA(null);
  };

  /**
   * Disable 2FA
   */
  const handleDisable2FA = async () => {
    if (!disablePassword) {
      setError2FA(t('profile.passwordRequired'));
      return;
    }
    
    setLoading2FA(true);
    setError2FA(null);
    try {
      await disable2FA(disablePassword, disableCode || undefined);
      setTwoFactorEnabled(false);
      setRecoveryCodesRemaining(0);
      setShowDisableModal(false);
      setDisablePassword('');
      setDisableCode('');
      message.success(t('profile.2faDisabled'));
    } catch (err) {
      setError2FA(err.message || t('profile.2faDisableFailed'));
    } finally {
      setLoading2FA(false);
    }
  };

  /**
   * Regenerate recovery codes
   */
  const handleRegenerateRecoveryCodes = async () => {
    Modal.confirm({
      title: t('profile.regenerateCodesTitle'),
      content: t('profile.regenerateCodesWarning'),
      icon: <WarningOutlined style={{ color: '#faad14' }} />,
      okType: 'danger',
      okText: t('profile.regenerate'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setLoading2FA(true);
        try {
          const result = await regenerateRecoveryCodes();
          setRecoveryCodes(result.recoveryCodes || []);
          setRecoveryCodesRemaining(result.recoveryCodes?.length || 10);
          setShowRecoveryCodes(true);
          message.success(t('profile.codesRegenerated'));
        } catch (err) {
          message.error(err.message || t('profile.regenerateFailed'));
        } finally {
          setLoading2FA(false);
        }
      }
    });
  };

  /**
   * Copy recovery codes to clipboard
   */
  const copyRecoveryCodes = () => {
    const codesText = recoveryCodes.join('\n');
    navigator.clipboard.writeText(codesText)
      .then(() => message.success(t('profile.codesCopied')))
      .catch(() => message.error(t('profile.copyFailed')));
  };

  // Show loading state
  if (authLoading && !user) {
    return (
      <PagesWrapper>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </PagesWrapper>
    );
  }

  // Show error state if no user
  if (!user) {
    return (
      <PagesWrapper>
        <Alert
          message={t('profile.notLoggedIn')}
          description={t('profile.pleaseLogin')}
          type="warning"
          showIcon
        />
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.profileContainer}>
        {/* Header Section with Avatar */}
        <Card className={styles.headerCard}>
          <div className={styles.headerContent}>
            <div className={styles.avatarSection}>
              <div className={styles.avatarWrapper}>
                {profileImageUrl ? (
                  <Avatar 
                    size={120} 
                    src={profileImageUrl}
                    className={styles.avatar}
                  />
                ) : (
                  <Avatar 
                    size={120} 
                    style={{ backgroundColor: avatarColor, fontSize: '40px' }}
                    className={styles.avatar}
                  >
                    {initials}
                  </Avatar>
                )}
                
                {/* Image upload/change overlay */}
                <div className={styles.avatarOverlay}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    className={styles.fileInput}
                    disabled={uploadingImage || deletingImage}
                  />
                  <Tooltip title={profileImageUrl ? t('profile.changeImage') : t('profile.uploadImage')}>
                    <Button
                      type="primary"
                      shape="circle"
                      icon={uploadingImage ? <Spin size="small" /> : <CameraOutlined />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage || deletingImage}
                      className={styles.uploadButton}
                    />
                  </Tooltip>
                  {profileImageUrl && (
                    <Tooltip title={t('profile.removeImage')}>
                      <Button
                        type="primary"
                        danger
                        shape="circle"
                        icon={deletingImage ? <Spin size="small" /> : <DeleteOutlined />}
                        onClick={handleImageDelete}
                        disabled={uploadingImage || deletingImage}
                        className={styles.deleteImageButton}
                      />
                    </Tooltip>
                  )}
                </div>
              </div>
              
              <div className={styles.userBasicInfo}>
                <Title level={2} className={styles.userName}>
                  {user.firstName && user.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user.firstName || user.email?.split('@')[0] || 'User'}
                </Title>
                <Text type="secondary" className={styles.userEmail}>
                  <MailOutlined /> {user.email}
                </Text>
                <div className={styles.roleTags}>
                  {user.roles?.map(role => {
                    const config = getRoleConfig(role);
                    return (
                      <Tag 
                        key={role} 
                        color={config.color}
                        icon={config.icon}
                        className={styles.roleTag}
                      >
                        {config.label}
                      </Tag>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </Card>

        <div className={styles.contentGrid}>
          {/* Account Information Card */}
          <Card 
            title={
              <Space>
                <IdcardOutlined />
                {t('profile.accountInfo')}
              </Space>
            }
            className={styles.infoCard}
          >
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label={
                <Space><IdcardOutlined />{t('profile.userId')}</Space>
              }>
                <Text copyable={{ text: user.id }} className={styles.userId}>
                  {user.id}
                </Text>
              </Descriptions.Item>
              
              <Descriptions.Item label={
                <Space><MailOutlined />{t('profile.email')}</Space>
              }>
                <Space>
                  {user.email}
                  {user.emailConfirmed ? (
                    <Tooltip title={t('profile.emailVerified')}>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    </Tooltip>
                  ) : (
                    <Tooltip title={t('profile.emailNotVerified')}>
                      <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    </Tooltip>
                  )}
                </Space>
              </Descriptions.Item>
              
              <Descriptions.Item label={
                <Space><PhoneOutlined />{t('profile.phone')}</Space>
              }>
                <Space>
                  {user.phoneNumber || <Text type="secondary">{t('profile.notSet')}</Text>}
                  {user.phoneNumber && (
                    user.phoneNumberConfirmed ? (
                      <Tooltip title={t('profile.phoneVerified')}>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t('profile.phoneNotVerified')}>
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      </Tooltip>
                    )
                  )}
                </Space>
              </Descriptions.Item>
              
              <Descriptions.Item label={
                <Space><GlobalOutlined />{t('profile.language')}</Space>
              }>
                {user.preferredLanguage?.toUpperCase() || 'EN'}
              </Descriptions.Item>
              
              <Descriptions.Item label={
                <Space><CalendarOutlined />{t('profile.memberSince')}</Space>
              }>
                {formatDate(user.createdAt)}
              </Descriptions.Item>
              
              <Descriptions.Item label={
                <Space><CalendarOutlined />{t('profile.lastLogin')}</Space>
              }>
                {formatDate(user.lastLoginAt)}
              </Descriptions.Item>
              
              <Descriptions.Item label={
                <Space><SafetyCertificateOutlined />{t('profile.twoFactor')}</Space>
              }>
                {user.twoFactorEnabled ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    {t('profile.enabled')}
                  </Tag>
                ) : (
                  <Tag color="default" icon={<CloseCircleOutlined />}>
                    {t('profile.disabled')}
                  </Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Edit Profile Card */}
          <Card 
            title={
              <Space>
                <EditOutlined />
                {t('profile.editProfile')}
              </Space>
            }
            className={styles.editCard}
            extra={
              !isEditingProfile && (
                <Button 
                  type="primary" 
                  icon={<EditOutlined />}
                  onClick={startEditProfile}
                >
                  {t('profile.edit')}
                </Button>
              )
            }
          >
            {isEditingProfile ? (
              <>
                {profileError && (
                  <Alert
                    message={profileError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setProfileError(null)}
                    className={styles.formAlert}
                  />
                )}
                
                <Form
                  form={profileForm}
                  layout="vertical"
                  onFinish={handleProfileSubmit}
                >
                  <Form.Item
                    name="firstName"
                    label={t('profile.firstName')}
                    rules={[
                      { max: 100, message: t('profile.firstNameTooLong') }
                    ]}
                  >
                    <Input 
                      prefix={<UserOutlined />}
                      placeholder={t('profile.firstNamePlaceholder')}
                      disabled={savingProfile}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="lastName"
                    label={t('profile.lastName')}
                    rules={[
                      { max: 100, message: t('profile.lastNameTooLong') }
                    ]}
                  >
                    <Input 
                      prefix={<UserOutlined />}
                      placeholder={t('profile.lastNamePlaceholder')}
                      disabled={savingProfile}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="phoneNumber"
                    label={t('profile.phoneNumber')}
                    rules={[
                      { 
                        pattern: /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/, 
                        message: t('profile.invalidPhone') 
                      }
                    ]}
                  >
                    <Input 
                      prefix={<PhoneOutlined />}
                      placeholder={t('profile.phonePlaceholder')}
                      disabled={savingProfile}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="preferredLanguage"
                    label={t('profile.preferredLanguage')}
                  >
                    <Select
                      disabled={savingProfile}
                      placeholder={t('profile.selectLanguage')}
                    >
                      {LANGUAGES.map(lang => (
                        <Option key={lang.code} value={lang.code}>
                          <Space>
                            <span>{lang.flag}</span>
                            <span>{lang.label}</span>
                          </Space>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  
                  <Form.Item className={styles.formActions}>
                    <Space>
                      <Button 
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={savingProfile}
                      >
                        {t('common.save')}
                      </Button>
                      <Button 
                        icon={<CloseOutlined />}
                        onClick={cancelEditProfile}
                        disabled={savingProfile}
                      >
                        {t('common.cancel')}
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </>
            ) : (
              <div className={styles.profilePreview}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label={t('profile.firstName')}>
                    {user.firstName || <Text type="secondary">{t('profile.notSet')}</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('profile.lastName')}>
                    {user.lastName || <Text type="secondary">{t('profile.notSet')}</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('profile.phoneNumber')}>
                    {user.phoneNumber || <Text type="secondary">{t('profile.notSet')}</Text>}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('profile.preferredLanguage')}>
                    {(() => {
                      const lang = LANGUAGES.find(l => l.code === (user.preferredLanguage || i18n.language));
                      return lang ? (
                        <Space>
                          <span>{lang.flag}</span>
                          <span>{lang.label}</span>
                        </Space>
                      ) : (
                        <Text type="secondary">{t('profile.notSet')}</Text>
                      );
                    })()}
                  </Descriptions.Item>
                </Descriptions>
               
              </div>
            )}
          </Card>

          {/* Change Password Card */}
          <Card 
            title={
              <Space>
                <LockOutlined />
                {t('profile.changePassword')}
              </Space>
            }
            className={styles.passwordCard}
            extra={
              !isEditingPassword && (
                <Button 
                  type="default"
                  icon={<LockOutlined />}
                  onClick={startEditPassword}
                >
                  {t('profile.change')}
                </Button>
              )
            }
          >
            {isEditingPassword ? (
              <>
                {passwordError && (
                  <Alert
                    message={passwordError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setPasswordError(null)}
                    className={styles.formAlert}
                  />
                )}
                
                <Form
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handlePasswordSubmit}
                >
                  <Form.Item
                    name="currentPassword"
                    label={t('profile.currentPassword')}
                    rules={[
                      { required: true, message: t('profile.currentPasswordRequired') }
                    ]}
                  >
                    <Input.Password 
                      prefix={<LockOutlined />}
                      placeholder={t('profile.currentPasswordPlaceholder')}
                      disabled={savingPassword}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="newPassword"
                    label={t('profile.newPassword')}
                    rules={[
                      { required: true, message: t('profile.newPasswordRequired') },
                      { min: 12, message: t('profile.passwordMinLength') },
                      { 
                        pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{12,}$/,
                        message: t('profile.passwordComplexity')
                      }
                    ]}
                    hasFeedback
                  >
                    <Input.Password 
                      prefix={<LockOutlined />}
                      placeholder={t('profile.newPasswordPlaceholder')}
                      disabled={savingPassword}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    name="confirmPassword"
                    label={t('profile.confirmPassword')}
                    dependencies={['newPassword']}
                    rules={[
                      { required: true, message: t('profile.confirmPasswordRequired') },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('newPassword') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t('profile.passwordMismatch')));
                        },
                      }),
                    ]}
                    hasFeedback
                  >
                    <Input.Password 
                      prefix={<LockOutlined />}
                      placeholder={t('profile.confirmPasswordPlaceholder')}
                      disabled={savingPassword}
                    />
                  </Form.Item>
                  
                  <Alert
                    message={t('profile.passwordRequirements')}
                    description={
                      <ul className={styles.passwordRequirements}>
                        <li>{t('profile.passwordReq1')}</li>
                        <li>{t('profile.passwordReq2')}</li>
                        <li>{t('profile.passwordReq3')}</li>
                        <li>{t('profile.passwordReq4')}</li>
                      </ul>
                    }
                    type="info"
                    showIcon
                    className={styles.passwordHint}
                  />
                  
                  <Form.Item className={styles.formActions}>
                    <Space>
                      <Button 
                        type="primary"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={savingPassword}
                      >
                        {t('profile.updatePassword')}
                      </Button>
                      <Button 
                        icon={<CloseOutlined />}
                        onClick={cancelEditPassword}
                        disabled={savingPassword}
                      >
                        {t('common.cancel')}
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </>
            ) : (
              <div className={styles.passwordInfo}>
                <Text type="secondary">
                  {t('profile.passwordSecurityNote')}
                </Text>
              </div>
            )}
          </Card>
        </div>

        {/* Security & Privacy - Consolidated Card */}
        <div className={styles.securitySection}>
          <Card 
            title={
              <Space>
                <SafetyCertificateOutlined />
                {t('profile.securityPrivacy')}
              </Space>
            }
            className={styles.securityCard}
          >
            {/* Two-Factor Authentication Section */}
            <div className={styles.securitySectionHeader}>
              <Space>
                <MobileOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                <Text strong style={{ fontSize: 16 }}>{t('profile.twoFactorAuth')}</Text>
              </Space>
              <Tag color={twoFactorEnabled ? 'green' : 'default'}>
                {twoFactorEnabled ? t('profile.enabled') : t('profile.disabled')}
              </Tag>
            </div>
            
            {error2FA && (
              <Alert
                message={error2FA}
                type="error"
                showIcon
                closable
                onClose={() => setError2FA(null)}
                className={styles.formAlert}
                style={{ marginBottom: 16 }}
              />
            )}

            {twoFactorEnabled ? (
              // 2FA is enabled - show status and management options
              <div className={styles.twoFactorStatus}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div className={styles.twoFactorInfo}>
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 24 }} />
                    <div>
                      <Text strong>{t('profile.2faActive')}</Text>
                      <br />
                      <Text type="secondary">{t('profile.2faActiveDescription')}</Text>
                    </div>
                  </div>
                  <br/>                  
                  <div className={styles.recoveryCodesInfo}>
                    <KeyOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                    <div>
                      <Text>{t('profile.recoveryCodesRemaining')}: </Text>
                      <Tag color={recoveryCodesRemaining <= 2 ? 'red' : recoveryCodesRemaining <= 5 ? 'orange' : 'green'}>
                        {recoveryCodesRemaining}
                      </Tag>
                      {recoveryCodesRemaining <= 2 && (
                        <Text type="danger" style={{ display: 'block', marginTop: 4 }}>
                          {t('profile.lowRecoveryCodes')}
                        </Text>
                      )}
                    </div>
                  </div>
                  
                  <Space wrap style={{ marginTop: 16 }}>
                    <Button 
                      icon={<ReloadOutlined />}
                      onClick={handleRegenerateRecoveryCodes}
                      loading={loading2FA}
                    >
                      {t('profile.regenerateCodes')}
                    </Button>
                    <Button 
                      danger
                      icon={<CloseCircleOutlined />}
                      onClick={() => setShowDisableModal(true)}
                    >
                      {t('profile.disable2FA')}
                    </Button>
                  </Space>
                </Space>
              </div>
            ) : (
              // 2FA is not enabled - show enable button and info
              <div className={styles.twoFactorDisabled}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  <div className={styles.twoFactorInfo}>
                    <SafetyCertificateOutlined style={{ color: '#1890ff', fontSize: 24 }} />
                    <div>
                      <Text>{t('profile.2faDescription')}</Text>
                    </div>
                  </div>
                  
                  <Alert
                    message={t('profile.2faRecommendation')}
                    description={t('profile.2faRecommendationDesc')}
                    type="info"
                    showIcon
                  />
                  
                  <Button 
                    type="primary"
                    icon={<MobileOutlined />}
                    onClick={start2FASetup}
                    loading={loading2FA}
                    size="large"
                  >
                    {t('profile.enable2FA')}
                  </Button>
                </Space>
              </div>
            )}

          {/* 2FA Setup Wizard Modal */}
          <Modal
            title={t('profile.setup2FA')}
            open={showSetupWizard}
            onCancel={close2FASetup}
            footer={null}
            width={520}
            destroyOnClose
          >
            <Steps
              current={setupStep}
              items={[
                { title: t('profile.scanQR') },
                { title: t('profile.verifyCode') },
                { title: t('profile.saveRecoveryCodes') },
              ]}
              style={{ marginBottom: 24 }}
            />
            
            {error2FA && (
              <Alert
                message={error2FA}
                type="error"
                showIcon
                closable
                onClose={() => setError2FA(null)}
                style={{ marginBottom: 16 }}
              />
            )}
            
            {setupStep === 0 && twoFactorSetupData && (
              // Step 1: Scan QR Code
              <div style={{ textAlign: 'center' }}>
                <Paragraph>{t('profile.scanQRInstructions')}</Paragraph>
                
                <div style={{ margin: '24px 0' }}>
                  <QRCode 
                    value={twoFactorSetupData.authenticatorUri || ''} 
                    size={200}
                    style={{ margin: '0 auto' }}
                  />
                </div>
                
                <Paragraph type="secondary">
                  {t('profile.cantScanQR')}
                </Paragraph>
                <Input.TextArea
                  value={twoFactorSetupData.sharedKey}
                  readOnly
                  autoSize
                  style={{ fontFamily: 'monospace', marginBottom: 16 }}
                />
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(twoFactorSetupData.sharedKey);
                    message.success(t('profile.keyCopied'));
                  }}
                  style={{ marginBottom: 16 }}
                >
                  {t('profile.copyKey')}
                </Button>
                
                <div style={{ marginTop: 24 }}>
                  <Button type="primary" onClick={() => setSetupStep(1)}>
                    {t('common.next')}
                  </Button>
                </div>
              </div>
            )}
            
            {setupStep === 1 && (
              // Step 2: Verify Code
              <div style={{ textAlign: 'center' }}>
                <Paragraph>{t('profile.enterCodeInstructions')}</Paragraph>
                
                <Input
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  style={{ 
                    fontFamily: 'monospace', 
                    fontSize: 24, 
                    textAlign: 'center',
                    letterSpacing: '0.3em',
                    width: 200,
                    margin: '24px auto'
                  }}
                  autoFocus
                />
                
                <div style={{ marginTop: 24 }}>
                  <Space>
                    <Button onClick={() => setSetupStep(0)}>
                      {t('common.back')}
                    </Button>
                    <Button 
                      type="primary" 
                      onClick={complete2FASetup}
                      loading={loading2FA}
                      disabled={verificationCode.length !== 6}
                    >
                      {t('profile.verifyAndEnable')}
                    </Button>
                  </Space>
                </div>
              </div>
            )}
            
            {setupStep === 2 && (
              // Step 3: Save Recovery Codes
              <div>
                <Alert
                  message={t('profile.saveCodesWarning')}
                  description={t('profile.saveCodesDescription')}
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                
                <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                  <List
                    grid={{ gutter: 16, column: 2 }}
                    dataSource={recoveryCodes}
                    renderItem={(code, index) => (
                      <List.Item>
                        <code style={{ fontFamily: 'monospace', fontSize: 14 }}>
                          {index + 1}. {code}
                        </code>
                      </List.Item>
                    )}
                  />
                </Card>
                
                <Space style={{ width: '100%', justifyContent: 'center' }}>
                  <Button icon={<CopyOutlined />} onClick={copyRecoveryCodes}>
                    {t('profile.copyCodes')}
                  </Button>
                </Space>
                
                <Divider />
                
                <div style={{ textAlign: 'center' }}>
                  <Button type="primary" onClick={close2FASetup}>
                    {t('profile.doneSetup')}
                  </Button>
                </div>
              </div>
            )}
          </Modal>

          {/* Disable 2FA Confirmation Modal */}
          <Modal
            title={t('profile.disable2FATitle')}
            open={showDisableModal}
            onCancel={() => {
              setShowDisableModal(false);
              setDisablePassword('');
              setDisableCode('');
              setError2FA(null);
            }}
            onOk={handleDisable2FA}
            okText={t('profile.disable2FA')}
            okType="danger"
            okButtonProps={{ loading: loading2FA }}
            destroyOnClose
          >
            {error2FA && (
              <Alert
                message={error2FA}
                type="error"
                showIcon
                closable
                onClose={() => setError2FA(null)}
                style={{ marginBottom: 16 }}
              />
            )}
            
            <Paragraph>{t('profile.disable2FAWarning')}</Paragraph>
            
            <Form layout="vertical">
              <Form.Item label={t('profile.currentPassword')} required>
                <Input.Password
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder={t('profile.currentPasswordPlaceholder')}
                />
              </Form.Item>
              <Form.Item 
                label={t('profile.verificationCodeOptional')}
                extra={t('profile.codeOptionalHint')}
              >
                <Input
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
            </Form>
          </Modal>

          {/* View Recovery Codes Modal */}
          <Modal
            title={t('profile.yourRecoveryCodes')}
            open={showRecoveryCodes}
            onCancel={() => setShowRecoveryCodes(false)}
            footer={[
              <Button key="copy" icon={<CopyOutlined />} onClick={copyRecoveryCodes}>
                {t('profile.copyCodes')}
              </Button>,
              <Button key="close" type="primary" onClick={() => setShowRecoveryCodes(false)}>
                {t('common.close')}
              </Button>
            ]}
          >
            <Alert
              message={t('profile.saveCodesWarning')}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Card size="small" style={{ background: '#fafafa' }}>
              <List
                grid={{ gutter: 16, column: 2 }}
                dataSource={recoveryCodes}
                renderItem={(code, index) => (
                  <List.Item>
                    <code style={{ fontFamily: 'monospace', fontSize: 14 }}>
                      {index + 1}. {code}
                    </code>
                  </List.Item>
                )}
              />
            </Card>
          </Modal>

            <Divider style={{ margin: '24px 0' }} />

            {/* Active Sessions Section */}
            <div className={styles.securitySectionHeader}>
              <Space>
                <LaptopOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                <Text strong style={{ fontSize: 16 }}>{t('profile.sessions.title')}</Text>
              </Space>
              <Space>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={loadSessions}
                  loading={loadingSessions}
                  size="small"
                >
                  {t('common.refresh')}
                </Button>
                {sessions.length > 1 && (
                  <Button 
                    danger
                    icon={<LogoutOutlined />}
                    onClick={handleRevokeOtherSessions}
                    loading={revokingOthers}
                    size="small"
                  >
                    {t('profile.sessions.logoutOthers')}
                  </Button>
                )}
              </Space>
            </div>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              {t('profile.sessions.description')}
            </Paragraph>

            {loadingSessions ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <Spin />
              </div>
            ) : sessions.length === 0 ? (
              <Alert
                message={t('profile.sessions.noSessions')}
                type="info"
                showIcon
              />
            ) : (
              <List
                dataSource={sessions}
                renderItem={(session) => (
                  <List.Item
                    key={session.id}
                    actions={[
                      session.isCurrent ? (
                        <Tag color="green" key="current">
                          {t('profile.sessions.currentSession')}
                        </Tag>
                      ) : (
                        <Button
                          key="revoke"
                          danger
                          size="small"
                          icon={<LogoutOutlined />}
                          loading={revokingSession === session.id}
                          onClick={() => handleRevokeSession(session.id)}
                        >
                          {t('profile.sessions.revoke')}
                        </Button>
                      )
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          icon={getDeviceIcon(session.deviceName)}
                          style={{ backgroundColor: session.isCurrent ? '#52c41a' : '#1890ff' }}
                        />
                      }
                      title={
                        <Space>
                          <Text strong>{session.deviceName || t('profile.sessions.unknownDevice')}</Text>
                          {session.isCurrent && (
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                          )}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          {session.ipAddress && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {t('profile.sessions.ipAddress')}: {session.ipAddress}
                            </Text>
                          )}
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('profile.sessions.lastActive')}: {new Date(session.lastActivityAt).toLocaleString()}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('profile.sessions.createdAt')}: {new Date(session.createdAt).toLocaleString()}
                          </Text>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}

            {sessions.length > 0 && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Button
                  danger
                  type="dashed"
                  icon={<WarningOutlined />}
                  onClick={handleRevokeAllSessions}
                  loading={revokingAll}
                >
                  {t('profile.sessions.logoutAll')}
                </Button>
                <Paragraph type="secondary" style={{ marginTop: 8, fontSize: 12 }}>
                  {t('profile.sessions.logoutAllDescription')}
                </Paragraph>
              </div>
            )}

            <Divider style={{ margin: '24px 0' }} />

            {/* Trusted Devices Section */}
            <div className={styles.securitySectionHeader}>
              <Space>
                <SafetyCertificateOutlined style={{ fontSize: 18, color: '#722ed1' }} />
                <Text strong style={{ fontSize: 16 }}>{t('profile.trustedDevices.title')}</Text>
              </Space>
              <Space>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={loadTrustedDevices}
                  loading={loadingTrustedDevices}
                  size="small"
                >
                  {t('common.refresh')}
                </Button>
                {trustedDevices.length > 0 && (
                  <Button 
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleRevokeAllTrustedDevices}
                    loading={revokingAllTrustedDevices}
                    size="small"
                  >
                    {t('profile.trustedDevices.revokeAll')}
                  </Button>
                )}
              </Space>
            </div>
            
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              {t('profile.trustedDevices.description')}
            </Paragraph>

            {loadingTrustedDevices ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <Spin />
              </div>
            ) : trustedDevices.length === 0 ? (
              <Alert
                message={t('profile.trustedDevices.noDevices')}
                description={t('profile.trustedDevices.noDevicesDescription')}
                type="info"
                showIcon
              />
            ) : (
              <List
                dataSource={trustedDevices}
                renderItem={(device) => (
                  <List.Item
                    key={device.id}
                    actions={[
                      device.isCurrent ? (
                        <Tag color="green" key="current">
                          {t('profile.trustedDevices.currentDevice')}
                        </Tag>
                      ) : (
                        <Button
                          key="revoke"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          loading={revokingTrustedDevice === device.id}
                          onClick={() => handleRevokeTrustedDevice(device.id)}
                        >
                          {t('profile.trustedDevices.revoke')}
                        </Button>
                      )
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar 
                          icon={getDeviceIcon(device.deviceName)}
                          style={{ backgroundColor: device.isCurrent ? '#52c41a' : '#722ed1' }}
                        />
                      }
                      title={
                        <Space>
                          <Text strong>{device.deviceName || t('profile.trustedDevices.unknownDevice')}</Text>
                          {device.isCurrent && (
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                          )}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          {device.ipAddress && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {t('profile.trustedDevices.ipAddress')}: {device.ipAddress}
                            </Text>
                          )}
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('profile.trustedDevices.trustedAt')}: {new Date(device.createdAt).toLocaleString()}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('profile.trustedDevices.expiresAt')}: {new Date(device.expiresAt).toLocaleString()}
                          </Text>
                          {device.lastUsedAt && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {t('profile.trustedDevices.lastUsed')}: {new Date(device.lastUsedAt).toLocaleString()}
                            </Text>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}

            <Divider style={{ margin: '24px 0' }} />

            {/* Login History Section */}
            <div className={styles.securitySectionHeader}>
              <Space>
                <HistoryOutlined style={{ fontSize: 18, color: '#fa8c16' }} />
                <Text strong style={{ fontSize: 16 }}>{t('profile.loginHistory.title')}</Text>
              </Space>
              <Button 
                icon={<ReloadOutlined />}
                onClick={loadLoginHistory}
                loading={loadingHistory}
                size="small"
              >
                {t('common.refresh')}
              </Button>
            </div>
            
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              {t('profile.loginHistory.description')}
            </Paragraph>

            {loadingHistory && loginHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <Spin />
              </div>
            ) : loginHistory.length === 0 ? (
              <Alert
                message={t('profile.loginHistory.noHistory')}
                type="info"
                showIcon
              />
            ) : (
              <>
                <List
                  size="small"
                  dataSource={loginHistory.slice(0, 5)}
                  renderItem={(event) => (
                    <List.Item key={event.id}>
                      <List.Item.Meta
                        avatar={
                          <Avatar 
                            icon={event.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            style={{ 
                              backgroundColor: event.success ? '#52c41a' : '#f5222d',
                              fontSize: 14
                            }}
                            size="small"
                          />
                        }
                        title={
                          <Space>
                            <Text strong style={{ fontSize: 13 }}>
                              {t(`profile.loginHistory.events.${event.eventType}`, { defaultValue: event.eventDescription })}
                            </Text>
                            <Tag color={event.success ? 'green' : 'red'} style={{ fontSize: 10, padding: '0 4px' }}>
                              {event.success ? t('common.success') : t('common.failed')}
                            </Tag>
                          </Space>
                        }
                        description={
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {new Date(event.timestamp).toLocaleString()}
                          </Text>
                        }
                      />
                    </List.Item>
                  )}
                />
                
                <div className={styles.viewAllLink}>
                  <Button 
                    type="link" 
                    icon={<EyeOutlined />}
                    onClick={() => setShowHistoryModal(true)}
                  >
                    {t('profile.loginHistory.viewAll')} ({loginHistoryCount} {t('profile.loginHistory.eventsCount')})
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>

        {/* Login History Modal with Filters */}
        <Modal
          title={
            <Space>
              <HistoryOutlined />
              {t('profile.loginHistory.title')}
            </Space>
          }
          open={showHistoryModal}
          onCancel={() => {
            setShowHistoryModal(false);
            // Reset to last month
            setHistoryDateRange([dayjs().subtract(1, 'month'), dayjs()]);
            setHistoryEventFilter('all');
          }}
          footer={[
            <Button 
              key="refresh" 
              icon={<ReloadOutlined />} 
              onClick={() => loadLoginHistory()}
              loading={loadingHistory}
            >
              {t('common.refresh')}
            </Button>,
            <Button key="close" type="primary" onClick={() => setShowHistoryModal(false)}>
              {t('common.close')}
            </Button>
          ]}
          width={720}
          styles={{ body: { maxHeight: '60vh', overflow: 'auto' } }}
        >
          {/* Loading state */}
          {loadingHistory ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <Spin size="large" />
              <Paragraph type="secondary" style={{ marginTop: 16 }}>
                {t('common.loading')}
              </Paragraph>
            </div>
          ) : (
            <>
              {/* Filters */}
              <div className={styles.historyModalFilters}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                <FilterOutlined /> {t('profile.loginHistory.dateRange')}
              </Text>
              <RangePicker
                value={historyDateRange}
                onChange={(dates) => {
                  setHistoryDateRange(dates);
                  loadLoginHistory(dates);
                }}
                style={{ width: '100%' }}
                allowClear
                format="YYYY-MM-DD"
                presets={[
                  { label: t('profile.loginHistory.presets.lastWeek'), value: [dayjs().subtract(1, 'week'), dayjs()] },
                  { label: t('profile.loginHistory.presets.lastMonth'), value: [dayjs().subtract(1, 'month'), dayjs()] },
                  { label: t('profile.loginHistory.presets.last3Months'), value: [dayjs().subtract(3, 'months'), dayjs()] },
                ]}
              />
            </div>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                <FilterOutlined /> {t('profile.loginHistory.eventType')}
              </Text>
              <Select
                value={historyEventFilter}
                onChange={setHistoryEventFilter}
                style={{ width: '100%' }}
              >
                <Option value="all">{t('profile.loginHistory.allEvents')}</Option>
                <Option value="success">{t('profile.loginHistory.successOnly')}</Option>
                <Option value="failed">{t('profile.loginHistory.failedOnly')}</Option>
                {eventTypes.map(type => (
                  <Option key={type} value={type}>
                    {t(`profile.loginHistory.events.${type}`, { defaultValue: type })}
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          {/* Results summary */}
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            {t('profile.loginHistory.showing')} {filteredLoginHistory.length} {t('profile.loginHistory.of')} {loginHistory.length} {t('profile.loginHistory.eventsCount')}
          </Paragraph>

          {/* Filtered list */}
          {filteredLoginHistory.length === 0 ? (
            <Empty description={t('profile.loginHistory.noMatchingEvents')} />
          ) : (
            <List
              dataSource={filteredLoginHistory}
              renderItem={(event) => (
                <List.Item key={event.id}>
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        icon={event.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        style={{ 
                          backgroundColor: event.success ? '#52c41a' : '#f5222d'
                        }}
                      />
                    }
                    title={
                      <Space wrap>
                        <Text strong>
                          {t(`profile.loginHistory.events.${event.eventType}`, { defaultValue: event.eventDescription })}
                        </Text>
                        <Tag color={event.success ? 'green' : 'red'}>
                          {event.success ? t('common.success') : t('common.failed')}
                        </Tag>
                      </Space>
                    }
                    description={
                      <div className={styles.historyEventMeta}>
                        <Text type="secondary">
                          <ClockCircleOutlined style={{ marginRight: 6 }} />
                          {new Date(event.timestamp).toLocaleString(undefined, {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </Text>
                        {event.deviceInfo && (
                          <Text type="secondary">
                            <LaptopOutlined style={{ marginRight: 6 }} />
                            {event.deviceInfo}
                          </Text>
                        )}
                        {event.ipAddress && (
                          <Text type="secondary">
                            <GlobalOutlined style={{ marginRight: 6 }} />
                            IP: {event.ipAddress}
                          </Text>
                        )}
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          )}
            </>
          )}
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default Profile;
